import { motion } from "framer-motion";
import { FlaskConical, Leaf, Recycle, Globe2 } from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_life-architecture/artifacts/q5vdg061_Logo%20Final.png";

export const BrandStrip = () => {
  return (
    <section
      data-testid="brand-strip"
      className="relative w-full overflow-hidden bg-gradient-to-b from-[#050907] via-[#030704] to-[#030704] border-b border-white/[0.05]"
    >
      {/* Ambient emerald glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[90%] w-[60%] rounded-full bg-[#10B981]/8 blur-[140px]" />
      </div>

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "120px 120px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 md:pt-40 pb-16 md:pb-24 flex flex-col items-center justify-center text-center">
        {/* Logo block */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          {/* Concentric glow rings */}
          <div className="absolute inset-0 -m-10 md:-m-16 rounded-full bg-[#10B981]/12 blur-3xl animate-pulse" />
          <div className="absolute inset-0 -m-4 md:-m-6 rounded-2xl ring-1 ring-[#10B981]/20" />

          <div className="relative h-40 w-40 sm:h-56 sm:w-56 md:h-72 md:w-72 lg:h-80 lg:w-80 rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_40px_120px_-20px_rgba(16,185,129,0.55)] bg-white">
            <img
              src={LOGO}
              alt="Colossus Biorefinery"
              className="h-full w-full object-contain"
              loading="eager"
            />
          </div>
        </motion.div>

        {/* Tagline line */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="mt-10 md:mt-14 flex items-center gap-4"
        >
          <span className="h-px w-10 md:w-16 bg-[#10B981]" />
          <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#10B981]">
            Proprietary Green-Science · Est. India
          </span>
          <span className="h-px w-10 md:w-16 bg-[#10B981]" />
        </motion.div>

        {/* Massive brand wordmark */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="font-brand text-white leading-[0.95] mt-6 md:mt-8"
          style={{ fontSize: "clamp(48px, 11vw, 180px)" }}
        >
          COLOSSUS<span className="text-[#10B981]">.</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="font-brand text-white/85 tracking-[0.25em] mt-2 md:mt-4"
          style={{ fontSize: "clamp(16px, 2.6vw, 42px)" }}
        >
          BIOREFINERY
        </motion.div>

        {/* Credentials strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-10 md:mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-8 w-full max-w-4xl"
        >
          {[
            { Icon: FlaskConical, label: ["Pharma", "Grade"] },
            { Icon: Leaf, label: ["100%", "Botanical"] },
            { Icon: Recycle, label: ["Closed", "Loop"] },
            { Icon: Globe2, label: ["Planet", "Positive"] },
          ].map(({ Icon, label }, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center gap-3"
            >
              <span className="relative inline-flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-full bg-[#10B981]/10 ring-1 ring-[#10B981]/30">
                <Icon className="h-5 w-5 md:h-6 md:w-6 text-[#10B981]" strokeWidth={1.5} />
              </span>
              <div className="leading-[1.15] text-[15px] md:text-[18px] uppercase tracking-[0.25em] text-white/70 font-body">
                <div>{label[0]}</div>
                <div>{label[1]}</div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.5] }}
          transition={{ duration: 2, delay: 1.4, repeat: Infinity, repeatType: "reverse" }}
          className="mt-14 md:mt-20 text-[10px] uppercase tracking-[0.4em] text-white/40"
        >
          ↓ Scroll to explore
        </motion.div>
      </div>
    </section>
  );
};

export default BrandStrip;
